import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams } from 'react-router-dom';

import { i18n } from '@lingui/core';
import { Trans, t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Grid } from 'semantic-ui-react';

import {
  maybeFetchConceptBasedAggregates,
  maybeFetchConceptWordcloud,
  maybeFetchTimeSeriesVisualizationsAggregates,
} from 'actions/view';
import { useTagLabelFormatter } from 'reducers/entityLabelFormatter';
import {
  conceptsSelector,
  entityLabelFormatterSelector,
  ontologiesByIdSelector,
  ontologiesItemsSelector,
} from 'selectors/entities';
import { aggregatesSelector, exploreDataSelector } from 'selectors/view';

import {
  NormalLargerSpacePaddedGrid,
  NormalSpacePaddedRow,
  SmallSpacePaddedGrid,
} from 'components/ui/Grid';
import Header from 'components/ui/Header';
import Segment from 'components/ui/Segment';
import ButtonGroup from 'components/ui/button/ButtonGroup';

import commonPropTypes from 'utils/commonPropTypes';

import * as svars from 'assets/style/variables';

import ViewFacetDashboardKpis from '../ViewFacetDashboardKpis';
import ConceptDrilldown from './ConceptDrilldown';
import {
  DISTRIBUTION_STATIC_DIMENSIONS,
  ExplorationBreakdownButtonWithModal,
} from './ExplorationBreakdownModal';
import ExplorationConceptFocus from './ExplorationConceptFocus';
import ExplorationModes from './ExplorationModes';
import ExplorationSecondaryNavbar from './ExplorationSecondaryNavbar';

function EmptyExplorationCase({ ontologyId, setOntologyId }) {
  const ontologiesItems = useSelector(ontologiesItemsSelector);
  console.log('ontologiesItems', ontologiesItems);

  return (
    <NormalLargerSpacePaddedGrid style={{ paddingTop: '10vh' }}>
      <Grid.Row>
        <Grid.Column width={16} textAlign="center">
          <Header>
            <Trans id="dashboard-explore.select-an-ontology-to-start-exploring" />
            .
          </Header>
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16} textAlign="center">
          <ButtonGroup
            items={ontologiesItems}
            onChange={(_, { value }) => setOntologyId(value)}
            value={ontologyId}
            centered
          />
        </Grid.Column>
      </Grid.Row>
    </NormalLargerSpacePaddedGrid>
  );
}

EmptyExplorationCase.propTypes = {
  ontologyId: PropTypes.string,
  setOntologyId: PropTypes.func.isRequired,
};
EmptyExplorationCase.defaultProps = {
  ontologyId: null,
};

function V2DashboardExplore({ hidden, viewFacet, locationQuery }) {
  const dispatch = useDispatch();
  const {
    kpis,
    productHierarchyKpis,
    tagKpis,
    generalDistributions,
    conceptKpis,
  } = useSelector(aggregatesSelector);
  const concepts = useSelector(conceptsSelector());
  const ontologies = useSelector(ontologiesByIdSelector);
  const tagLabelFormatter = useTagLabelFormatter();
  const entityLabelFormatter = useSelector(entityLabelFormatterSelector);

  const [ontologyId, setOntologyId] = useState(
    concepts?.[locationQuery.concept]?.ontologyId ||
      (Object.keys(ontologies) === 1 && Object.keys(ontologies)[0]) ||
      null
  );
  const [selectedConceptItem, setSelectedConceptItem] = useState(undefined);
  const [selectedGlobalDistribution, setSelectedGlobalDistribution] =
    useState(null);

  const { mappedData, customTreemapData, customTreemapDataLeavesOnly } =
    useSelector(exploreDataSelector)(ontologyId, selectedConceptItem?.id);

  const fetchConceptAggregates = useCallback((viewFacetId, conceptId) => {
    // Do not try to fetch if navigsting back to root (e.g. no concept)
    if (!conceptId) return;
    dispatch(maybeFetchConceptWordcloud(viewFacetId, conceptId));
    dispatch(
      maybeFetchConceptBasedAggregates(
        viewFacetId,
        conceptId,
        'concept_product_kpi'
      )
    );
    dispatch(
      maybeFetchConceptBasedAggregates(
        viewFacetId,
        conceptId,
        'concept_source_kpi'
      )
    );
    dispatch(
      maybeFetchConceptBasedAggregates(
        viewFacetId,
        conceptId,
        'concept_tag_kpi'
      )
    );
    dispatch(
      maybeFetchConceptBasedAggregates(viewFacetId, conceptId, 'concept_ts')
    );
  }, []);
  const onSelectConcept = useCallback(
    (id, withUnselect = false) => {
      if (id == null) {
        setSelectedConceptItem(undefined);
      }

      const item = customTreemapData?.find(
        (treemapItem) => treemapItem?.id === id
      );
      if (
        id == null ||
        (item && item === selectedConceptItem && withUnselect)
      ) {
        setSelectedConceptItem(undefined);
      } else {
        setSelectedConceptItem(item);
        fetchConceptAggregates(viewFacet.id, id);
      }
    },
    [viewFacet.id, customTreemapData, selectedConceptItem]
  );

  useEffect(() => {
    dispatch(
      maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'general_dist')
    );
    dispatch(
      maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'product_kpi')
    );
    dispatch(
      maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'concept_kpi')
    );
    dispatch(
      maybeFetchTimeSeriesVisualizationsAggregates(viewFacet.id, 'tag_kpi')
    );
    // If parameters were given by previous link, scroll to top
    if (locationQuery.concept) {
      fetchConceptAggregates(viewFacet.id, locationQuery.concept);
    }
  }, []);
  useEffect(() => {
    if (
      Object.keys(ontologies).length &&
      // Do not compute if no ontology is selected so exploration does not display
      ontologyId &&
      conceptKpis &&
      locationQuery.concept
    ) {
      const conceptId = locationQuery.concept;
      // Possibly update ontologyId if conceptId is from another ontology
      let newOntologyId = concepts?.[conceptId]?.ontologyId;
      if (newOntologyId && newOntologyId !== ontologyId) {
        setOntologyId(newOntologyId);
      } else {
        newOntologyId = ontologyId;
      }
      if (conceptId) {
        setSelectedConceptItem(
          customTreemapData.find((item) => item.id === conceptId)
        );
        fetchConceptAggregates(viewFacet.id, conceptId);
      }
    }
  }, [locationQuery, conceptKpis, concepts, ontologies, ontologyId]);

  const onSelectOntology = useCallback((_, { value }) => {
    setOntologyId(value);
    setSelectedConceptItem(undefined);
  }, []);

  const getSearchRouteParameters = useCallback(
    (texts, newWordcloudPolarity) => {
      const filters = {};
      if (texts.length) {
        filters.text = texts;
      }
      if (newWordcloudPolarity === 'all') {
        filters.positive = 1;
        filters.negative = 1;
      } else if (newWordcloudPolarity) {
        filters[newWordcloudPolarity] = 1;
      }
      if (selectedConceptItem) {
        filters.concept = selectedConceptItem.id;
      }
      return `/facets/analyze/${viewFacet.id}/search?${createSearchParams({
        ...filters,
        phg: viewFacet.base_product_hierarchy_group.id,
      })}`;
    },
    [viewFacet.id, selectedConceptItem]
  );

  const getEntityLabelFormatter = (entityName) => (item) =>
    entityLabelFormatter(entityName, item);

  const conceptLabelFormatter = (conceptId) =>
    entityLabelFormatter('concept', conceptId);

  const onSelectGlobalDistribution = (event, { value }) => {
    setSelectedGlobalDistribution(value);
  };

  const closeGlobalDistributionModal = () =>
    setSelectedGlobalDistribution(null);

  // Test - do not unmount rendered components when hidden
  // To avoid losing their states
  // Notably the selected tag could and selected ontology term
  // TODO : confirm it works or come back to umount for these components
  if (hidden || !ontologies) {
    return null;
  }

  const singleAnalyzedOntology = Object.keys(ontologies).length === 1;
  const distributionItem = DISTRIBUTION_STATIC_DIMENSIONS.find(
    ({ value }) => value === selectedGlobalDistribution
  );
  let breakdownModalProps = {};
  if (distributionItem) {
    breakdownModalProps = {
      data:
        selectedGlobalDistribution === 'source'
          ? generalDistributions?.source
          : productHierarchyKpis?.kpis,
      categoryDataKey: selectedGlobalDistribution,
      disabled: !productHierarchyKpis?.kpis?.length,
      labelFormatter: getEntityLabelFormatter(selectedGlobalDistribution),
      categoryHeader: i18n._(distributionItem?.text),
    };
  } else if (selectedGlobalDistribution) {
    breakdownModalProps = {
      data: tagKpis?.kpis?.[selectedGlobalDistribution],
      categoryDataKey: 'tag',
      disabled: !Object.keys(tagKpis?.kpis || {}).length,
      categoryHeader: entityLabelFormatter(
        'tagSet',
        selectedGlobalDistribution
      ),
      labelFormatter: tagLabelFormatter,
    };
  }
  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        display: hidden ? 'none' : 'block',
      }}
    >
      <SmallSpacePaddedGrid>
        <NormalSpacePaddedRow
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            borderBottom: `1px solid ${svars.colorLightGrey}`,
            paddingBottom: svars.spaceMedium,
          }}
        >
          <div style={{ display: 'flex' }}>
            <ViewFacetDashboardKpis viewFacet={viewFacet} />
            <ExplorationBreakdownButtonWithModal
              withReviewScore={viewFacet.use_review_score}
              title={t({ id: 'dashboard-explore.distribution-modal-title' })}
              globalAverageSentiment={kpis?.kpis?.[0]?.average_score || 0}
              modalIsOpen={selectedGlobalDistribution}
              onClose={closeGlobalDistributionModal}
              onDistributionChange={onSelectGlobalDistribution}
              currentDistribution={selectedGlobalDistribution}
              style={{ marginLeft: svars.spaceMedium }}
              {...breakdownModalProps}
            />
          </div>
        </NormalSpacePaddedRow>
        {ontologyId ? null : (
          <EmptyExplorationCase
            ontologyId={ontologyId}
            setOntologyId={setOntologyId}
          />
        )}

        {ontologyId && mappedData ? (
          <>
            <ExplorationSecondaryNavbar
              ontologyId={ontologyId}
              conceptId={selectedConceptItem?.id}
              selectedConceptItem={selectedConceptItem}
              singleAnalyzedOntology={singleAnalyzedOntology}
              onSelectOntology={onSelectOntology}
              onSelectConcept={onSelectConcept}
              getSearchRouteParameters={getSearchRouteParameters}
            />
            <NormalSpacePaddedRow
              style={{
                paddingTop: svars.spaceMedium,
                paddingBottom: 0,
                // Required to avoid glitch between loading state and time series display
                minHeight: '410px',
              }}
            >
              <Grid.Column width={9} stretched>
                <Segment>
                  {ontologyId && (
                    <ExplorationModes
                      ontologyId={ontologyId}
                      conceptId={selectedConceptItem?.id}
                      labelSelector={conceptLabelFormatter}
                      customTreemapData={customTreemapData}
                      customTreemapDataLeavesOnly={customTreemapDataLeavesOnly}
                      selectedConceptItem={selectedConceptItem}
                      onSelectConcept={onSelectConcept}
                      conceptLabelFormatter={conceptLabelFormatter}
                      viewFacetId={viewFacet.id}
                      fetchConceptAggregates={fetchConceptAggregates}
                      withReviewScore={viewFacet.use_review_score}
                    />
                  )}
                </Segment>
              </Grid.Column>
              <Grid.Column width={7} stretched>
                <Segment>
                  <ExplorationConceptFocus
                    viewFacetId={viewFacet?.id}
                    conceptId={selectedConceptItem?.id}
                    tagLabelFormatter={tagLabelFormatter}
                    entityLabelFormatter={entityLabelFormatter}
                    ontologyId={ontologyId}
                    onSelectConcept={onSelectConcept}
                  />
                </Segment>
              </Grid.Column>
            </NormalSpacePaddedRow>
            <ConceptDrilldown
              key={selectedConceptItem?.id}
              selectedConceptItem={selectedConceptItem}
              getSearchRouteParameters={getSearchRouteParameters}
            />
          </>
        ) : null}
      </SmallSpacePaddedGrid>
    </div>
  );
}

V2DashboardExplore.propTypes = {
  viewFacet: commonPropTypes.viewFacet.isRequired,
  hidden: PropTypes.bool,
  locationQuery: PropTypes.shape({
    concept: PropTypes.string,
  }).isRequired,
};

V2DashboardExplore.defaultProps = {
  hidden: false,
};

export default V2DashboardExplore;
